import { authHeaders } from "@/lib/api";
import { InventoryApiError, type AuditEvent, type SerialMode } from "./api";
const BASE = "/api/admin/inventory";
export type CountStatus = "OPEN" | "SUBMITTED" | "PENDING_APPROVAL" | "APPROVED" | "APPLIED" | "REJECTED";
export interface CountScope { location_ids: number[]; product_id: number }
export interface CountSummary {
  count_id: string; kind: "FULL" | "CYCLE"; scope: CountScope; reason: string;
  status: CountStatus; row_version: number; recount_of: string | null;
  snapshot_hash: string; created_by: string; created_at: string; submitted_by?: string | null;
}
export interface CountLine {
  box_id: string; product_id: number; location_id: number; location_seen: number | null;
  expected_qty: number; observed_qty: number | null; delta: number | null; state: "COUNTED" | "NOT_COUNTED";
  serial_policy: SerialMode; expected_serials: string[]; observed_serials: string[] | null;
  missing_serials: string[] | null; missing_unit_ids: string[] | null; unexpected_serials: string[] | null;
  exceptions: string[]; expected_box_version: number; condition: string; policy_version: number;
}
export interface CountObservation {
  observation_id: number; box_id: string; observed_qty: number; location_seen: number;
  serials: { raw: string; canonical: string }[]; exceptions: string[]; supersedes_id: number | null;
  actor: string; reason: string | null; created_at: string;
}
export interface Reconciliation {
  reconciliation_id: string; count_id: string; status: CountStatus; row_version: number;
  proposed_by: string; reviewed_by: string | null; reason: string; review_reason: string | null;
  rejected_by?: string | null; rejection_reason?: string | null;
}
export interface CountDetail extends CountSummary {
  lines: CountLine[]; total: number;
  exceptions: { box_id: string; observation_id: number; exceptions: string[] }[]; exception_total: number;
  observations: CountObservation[]; observation_total: number; events: AuditEvent[];
  reconciliation: Reconciliation | null;
}
export interface CreateCountInput extends CountScope { kind: "FULL" | "CYCLE"; reason: string; expected_version: number; recount_of?: string }
export interface ScanCountInput { box_id: string; observed_qty: number; serials: string[]; location_seen: number; expected_version: number; correction_of?: number; reason?: string }
export interface SubmitCountInput { completed_scope: boolean; zero_unobserved: string[]; acknowledge_not_counted: boolean; reason: string; expected_version: number }
export interface CountReason { reason: string; expected_version: number }
export interface CountMutation { count_id: string; status: CountStatus; row_version: number; observation_id?: number; reconciliation_id?: string }
async function failure(r: Response): Promise<never> {
  let detail = "";
  try { const b = await r.json(); detail = typeof b.detail === "string" ? b.detail : JSON.stringify(b.detail ?? ""); } catch { /* Keep the status even for HTML errors. */ }
  throw new InventoryApiError(r.status, detail);
}
async function request<T>(path: string, body?: object, key?: string): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    credentials: "include", method: body ? "POST" : "GET",
    headers: { "Content-Type": "application/json", ...authHeaders(), ...(body ? { "Idempotency-Key": key!, "X-Inventory-Confirm": "true" } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!r.ok) return failure(r);
  return r.json() as Promise<T>;
}
export function listCounts(limit = 25, offset = 0): Promise<{ counts: CountSummary[]; total: number; offset: number }> {
  return request(`/counts?${new URLSearchParams({ limit: String(limit), offset: String(offset) })}`);
}
export async function getCount(id: string, limit = 25, offset = 0, observationOffset = 0): Promise<CountDetail> {
  const detail = await request<CountDetail>(`/counts/${encodeURIComponent(id)}?${new URLSearchParams({ limit: String(limit), offset: String(offset), observation_limit: String(limit), observation_offset: String(observationOffset) })}`);
  if (!detail || detail.count_id !== id) throw new InventoryApiError(502, "Unexpected count response");
  return detail;
}
// Newlines delimit evidence, never normalize the values within each line.
export function parseCountSerials(text: string): string[] | null {
  const values = text === "" ? [] : text.split(/\r?\n/);
  return values.length <= 10000 && values.every(raw => /^[ -~]{1,160}$/.test(raw) && /[^ ]/.test(raw)) ? values : null;
}
// exception_total counts outside-scope observations only, not in-scope holds.
// Read every expected line in bounded, version-consistent pages before offering
// approval/application. This is UI readiness, never independent-review authority.
export async function countApprovalReady(seed: CountDetail, offset = 0, current = () => true): Promise<boolean> {
  if (!["PENDING_APPROVAL", "APPROVED"].includes(seed.status)) return false;
  if (!seed.reconciliation || seed.reconciliation.status !== seed.status || seed.reconciliation.count_id !== seed.count_id || !/^REC-[a-f0-9]{32}$/.test(seed.reconciliation.reconciliation_id) || !Number.isSafeInteger(seed.reconciliation.row_version) || seed.reconciliation.row_version < 0) return false;
  if (!Number.isSafeInteger(seed.observation_total) || seed.observation_total < 0) return false;
  if (!Number.isSafeInteger(seed.total) || seed.total < 0 || seed.total > 10000 || seed.exception_total !== 0 || !Array.isArray(seed.exceptions) || seed.exceptions.length !== 0) return false;
  if (!Number.isSafeInteger(seed.row_version) || seed.row_version < 0 || typeof seed.snapshot_hash !== "string" || !seed.snapshot_hash) return false;
  const stamp = (d: CountDetail) => JSON.stringify([d.count_id, d.row_version, d.snapshot_hash, d.status, d.scope, d.total, d.exception_total, d.observation_total, d.reconciliation]);
  const expected = stamp(seed);
  const seen = new Set<string>();
  for (let pageOffset = 0; pageOffset < Math.max(1, seed.total); pageOffset += 25) {
    if (!current()) return false;
    const page = pageOffset === offset ? seed : await getCount(seed.count_id, 25, pageOffset, 0);
    if (!current() || stamp(page) !== expected || !Array.isArray(page.lines) || page.lines.length !== Math.min(25, seed.total - pageOffset) || !Array.isArray(page.exceptions) || page.exceptions.length !== 0) return false;
    for (const line of page.lines) {
      if (typeof line.box_id !== "string" || !line.box_id || seen.has(line.box_id)) return false;
      seen.add(line.box_id);
      if (line.state !== "COUNTED" || !Number.isSafeInteger(line.observed_qty) || line.observed_qty! < 0 || !Number.isSafeInteger(line.location_seen) || line.location_seen !== line.location_id || !Array.isArray(line.exceptions) || line.exceptions.length > 0 || !Array.isArray(line.observed_serials) || !Array.isArray(line.missing_serials) || !Array.isArray(line.unexpected_serials) || line.unexpected_serials.length > 0) return false;
    }
  }
  return seen.size === seed.total;
}
export function scanCount(id: string, input: ScanCountInput, key: string): Promise<CountMutation> {
  const { box_id, observed_qty, serials, location_seen, expected_version, correction_of, reason } = input;
  return request(`/counts/${encodeURIComponent(id)}/scan`, { box_id, observed_qty, serials, location_seen, expected_version, ...(correction_of === undefined ? {} : { correction_of }), ...(reason === undefined ? {} : { reason }) }, key);
}
export function submitCount(id: string, input: SubmitCountInput, key: string): Promise<CountMutation> {
  const { completed_scope, zero_unobserved, acknowledge_not_counted, reason, expected_version } = input;
  return request(`/counts/${encodeURIComponent(id)}/submit`, { completed_scope, zero_unobserved, acknowledge_not_counted, reason, expected_version }, key);
}
export function reconcileCount(id: string, input: CountReason, key: string): Promise<CountMutation> {
  return request(`/counts/${encodeURIComponent(id)}/reconcile`, { reason: input.reason, expected_version: input.expected_version }, key);
}
export function reviewReconciliation(id: string, action: "approve" | "reject" | "apply", input: CountReason, key: string): Promise<CountMutation> {
  return request(`/reconciliations/${encodeURIComponent(id)}/${action}`, { reason: input.reason, expected_version: input.expected_version }, key);
}
export async function downloadCountReport(id: string, format: "csv" | "pdf", current = () => true): Promise<void> {
  // Filenames come only from a validated opaque ID, never Content-Disposition.
  if (!/^CNT-[a-f0-9]{32}$/.test(id)) throw new InventoryApiError(422, "Invalid count identifier");
  const r = await fetch(`${BASE}/counts/${id}/export?format=${format}`, { credentials: "include", headers: authHeaders() });
  if (!current()) return;
  if (!r.ok) return failure(r);
  const mime = r.headers.get("Content-Type")?.split(";")[0]?.trim().toLowerCase();
  if (mime !== (format === "pdf" ? "application/pdf" : "text/csv")) throw new InventoryApiError(502, "Unexpected report content type");
  const blob = await r.blob();
  if (!current()) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  try { a.href = url; a.download = `${id}.${format}`; document.body.append(a); a.click(); }
  finally { a.remove(); URL.revokeObjectURL(url); }
}
export function createCount(input: CreateCountInput, key: string): Promise<CountMutation> {
  const { location_ids, product_id, kind, reason, expected_version, recount_of } = input;
  return request("/counts", { location_ids, product_id, kind, reason, expected_version, ...(recount_of === undefined ? {} : { recount_of }) }, key);
}
